import { useToast } from '@cad-arc';
import {
    UnitStatusSearchGetResponse,
    SavedUnitStatusSearchDto,
    CadSearchsaveResource,
    UnitStatusSearchForm,
} from '@mark43/cad-api';
import { useEnabledFeatures } from '../common/hooks/enabled-features/use-enabled-features';
import { createApiErrorHandler } from '@m43/core';
import { useApiClient } from '~/api-client';
import { QueryObserverResult, useMutation } from '@tanstack/react-query';
import { errorOccurredWhile } from '../common/helpers/error-occurred-while';
import { formatUnitStatusSearchForm } from './format-unit-status-search-form';
import { SavedSearch } from '../search-windows/saved-search';
import { SavedSearchHandler } from '../fr-page-layout/search-pages/types';

export function SavedUnitStatusSearch({
    savedSearch,
    refetchSavedSearches,
    onSavedSearchClick,
}: Readonly<{
    savedSearch: SavedUnitStatusSearchDto;
    refetchSavedSearches: () => Promise<
        QueryObserverResult<UnitStatusSearchGetResponse | undefined>
    >;
    onSavedSearchClick: SavedSearchHandler<UnitStatusSearchForm>;
}>) {
    const apiClient = useApiClient();
    const toast = useToast();
    const { subpremiseSupport } = useEnabledFeatures();
    const deleteSavedSearchMutation = useMutation((savedSearchId: number) => {
        return apiClient
            .request<CadSearchsaveResource.DeleteSavedUnitStatusSearch>({
                method: 'POST',
                url: 'cad_search/save/unit_status/delete',
                data: { savedSearchId },
            })
            .catch(
                createApiErrorHandler(() => {
                    toast({
                        status: 'error',
                        description: errorOccurredWhile('deleting saved search'),
                    });
                })
            );
    });
    const updateSavedSearchNameMutation = useMutation(
        ({ savedSearchId, newName }: { savedSearchId: number; newName: string }) => {
            return apiClient
                .request<CadSearchsaveResource.UpdateUnitStatusSearchName>({
                    method: 'PUT',
                    url: 'cad_search/save/unit_status/name',
                    data: { savedSearchId, newName },
                })
                .catch(
                    createApiErrorHandler(() => {
                        toast({
                            status: 'error',
                            description: errorOccurredWhile('updating saved search name'),
                        });
                    })
                );
        }
    );

    return (
        <SavedSearch
            savedSearchId={savedSearch.savedSearchId}
            savedSearchName={savedSearch.savedSearchName}
            lastExecutedDateUtc={savedSearch.lastExecutedDateUtc}
            formattedParams={formatUnitStatusSearchForm(
                savedSearch.unitStatusSearchForm,
                subpremiseSupport
            )}
            runSavedSearch={() =>
                onSavedSearchClick({
                    search: savedSearch.unitStatusSearchForm,
                    savedSearchId: savedSearch.savedSearchId,
                })
            }
            updateSavedSearchNameMutation={updateSavedSearchNameMutation}
            deleteSavedSearch={() =>
                deleteSavedSearchMutation.mutateAsync(savedSearch.savedSearchId)
            }
            refetchSavedSearches={refetchSavedSearches}
        />
    );
}
